import type { PaginationItemCounterProps } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    useStyles,
    Grid,
    PaginationItem,
    Flex,
    Pagination,
    pxToRem,
} from '@archipro-design/aria';
import { Link, useLocation, useSearchParams } from '@remix-run/react';
import type { PaginationState } from '@modules/pagination/type';
import * as S from './TileGrid.style';
import type { ReactElement, RefObject } from 'react';
import { useRef, useEffect } from 'react';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import type { CategoryTypes } from '../../type';
import { AnimatedGridClass } from '@modules/category/component/collapsible-page-layout/CollapsiblePageLayout.style';
import cx from 'classnames';

interface TileGridProps {
    items: ReactElement[];
    columns?: string | number;
    columnWidth?: number;
    pagination: PaginationState;
    paginationItem?: string;
    onPageChange?: (page: number) => void;
    tileGap?: number;
    rowGap?: number;
    type?: CategoryTypes;
    projectsRef?: RefObject<HTMLDivElement>;
    gridRef?: RefObject<HTMLDivElement>;
}

export const TileGrid = (props: TileGridProps) => {
    const {
        items,
        columns = 3,
        columnWidth,
        pagination,
        paginationItem = 'items',
        onPageChange,
        tileGap = 10,
        rowGap = 96,
        type,
        projectsRef,
        gridRef,
    } = props;
    const { css } = useStyles({
        totalPages: pagination.totalCount,
        tileGap: tileGap,
        rowGap: rowGap,
        smallRowGap: type && type === 'products',
    });
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const appDisplayMode = useAppDisplayModeContext();

    const createPrefetchLink = (page: number) => {
        return trimPaginationURL(page, searchParams, location);
    };

    // ref to avoid the value update trigger the effect callback
    const changingPage = useRef(false);
    const currentPageNum = searchParams?.get('page');

    useEffect(() => {
        if (changingPage.current) {
            projectsRef?.current?.scrollIntoView();
        }
        changingPage.current = false;
    }, [currentPageNum, projectsRef]);

    return (
        <div className={css(S.TileGridWrapper)}>
            <Grid
                ref={gridRef}
                className={cx(css(S.GridWrapper), AnimatedGridClass)}
                columns={
                    columnWidth && appDisplayMode.desktop
                        ? `repeat(${columns}, ${pxToRem(columnWidth)})`
                        : columns
                }
            >
                {items}
            </Grid>
            {pagination.totalPages > 1 ? (
                <Flex className={css(S.PaginationWrapper)} hAlign="center" fill>
                    <Pagination
                        prevButton={{
                            as: Link,
                            prefetch: 'intent',
                            key: 'prevButton',
                            to: createPrefetchLink(pagination.prevPage),
                        }}
                        nextButton={{
                            as: Link,
                            prefetch: 'intent',
                            key: 'nextButton',
                            to: createPrefetchLink(pagination.nextPage),
                            disabled: pagination.isLastPage,
                        }}
                        renderItem={(item) => {
                            const disablePrev =
                                item.type === 'previous' &&
                                pagination.isFirstPage;
                            const disableNext =
                                item.type === 'next' && pagination.isLastPage;

                            if (disablePrev || disableNext) {
                                return PaginationItem.create({
                                    key: `pagination-item-${item.page}-${item.type}`,
                                    content: item,
                                });
                            }

                            return PaginationItem.create({
                                key: `pagination-item-${item.page}-${item.type}`,
                                as: Link,
                                prefetch: 'intent',
                                to: createPrefetchLink(item.page),
                                content: item,
                            });
                        }}
                        siblingCount={1}
                        boundaryCount={1}
                        count={pagination.totalPages}
                        counter={{
                            range: {
                                from: pagination.currentRange.from,
                                to: pagination.currentRange.to,
                            },
                            total: pagination.totalCount,
                            textRenderer: (
                                range: PaginationItemCounterProps['range'],
                                total: PaginationItemCounterProps['total']
                            ) =>
                                `${range.from} - ${range.to} of ${total} ${paginationItem}`,
                        }}
                        totalCount={pagination.totalCount}
                        defaultPage={pagination.page}
                        page={pagination.page}
                        pageSize={pagination.pageSize}
                        appDisplayMode={appDisplayMode}
                        onChange={(_, page) => {
                            changingPage.current = true;
                            onPageChange && page !== null && onPageChange(page);
                        }}
                    />
                </Flex>
            ) : null}
        </div>
    );
};
